"use client"

import 'react-photo-view/dist/react-photo-view.css';
import './style.css';
import React, {
  useCallback,
  useEffect,
  memo,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useVirtualizer,
  type Virtualizer,
} from "@tanstack/react-virtual";
import dynamic from "next/dynamic";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Button } from "@/components/fun-ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Col,
  Paper,
  PaperId,
  SetPaperColsMode,
} from "@/stores/slices/paperSlice"
import {
  useAiCol,
  useAiColsConfig,
  useCols,
  useFetchPapers,
  useFilters,
  usePagination,
  usePapers,
  useListCitationStyleKey,
  useListCitationStyleUseDefault,
  usePapersCitationStyle,
  usePapersPinQuota,
  useSubjects,
  useUser,
  useUserVip,
  useUploadSessions,
  useCitationStylesConfig,
} from "@/stores/appStore"
import Loading from "@/components/Loading";
import { useFetchPapersPolling } from "@/hooks/useFetchPapersPolling";
import { Tip } from "@/components/fun-ui/Tip";
import { HelpCircleIcon, LetterIcon } from "@/components/fun-ui/icons";
import { PaperUpload } from "@/components/paper-upload";
import { importPlugin } from "@/components/paper-upload/plugins/import-plugin";
import TableFooterbar from "@/components/papers/PaperList/TableFooterbar";
import usePathParams from "@/components/subjects/hooks/usePathParams";
import { useAiColSidebarCollapsed, usePapersGridMode } from "@/stores/localStore";
import { PapersFeatures } from "./PapersFeatures";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  // DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { RadioGroup, RadioGroupItem } from "@/components/fun-ui/RadioGroup";
import { AIColsSelector } from "./AIColsSelector";
import { CustomAIColForm } from "./CreateCustomAICol";
import { UserTriggeredDialog } from "@/components/guides/GuidesDialog";
import { Label } from "@/components/ui/label";
import { useSelectionActions } from '@/hooks/useSelection';
import { useUpgradeVip } from "@/components/payment/UpgradeVipButton";
import { chartColKeys } from './lib/aiColsConst';
import { SemiCircleWithShadow } from './SemiCircleWithShadow';
import { Toolbar } from './Toolbar';
import { getColConsumePrice } from './lib/getColConsumePrice';
import { track } from '@/lib/track';
import { toast } from 'react-hot-toast';
import { useTableColumns } from './hooks';
import { OnBeforeUpload } from '@/components/paper-upload/hooks/useUpload';
import {
  useCiteproc,
  SupportedStyleKey,
  registerCitationStyles,
  API_KEY_TO_STYLE_KEY,
  toCitationStyleApiKey,
  toSupportedStyleKey,
  diffCitationStyleApiKeys,
} from '@/hooks/useCiteproc';
import {
  CitationStyleProvider,
  type CiteprocSelectOption,
  type CitationStyleContextValue,
} from './CitationStyleContext';
import { api } from '@/lib/env';
import { fun } from '@/lib/request';
import type { WrapResponse } from '@/types';
import type { ResultViewRetryPayload } from './ResultView';

export type PaperListProps = {
  className?: string;
};

type PaperPinPayload = {
  new_pinned_count?: number;
  pinned_count?: number;
  pin_limit?: number;
};

type PinAction = 'pin' | 'unpin';

const ConfirmPriceDialog = dynamic(
  () => import("@/components/papers/PaperList/ConfirmPriceDialog").then((mod) => mod.ConfirmPriceDialog),
  { ssr: false },
);

type AIColsSidebarContentProps = {
  collapsed: boolean;
  vip: ReturnType<typeof useUserVip>["vip"];
  aiColsConfig: ReturnType<typeof useAiColsConfig>["value"];
  cols: Col[];
  onSelected: (colKeys: string[], cols: Col[], mode?: SetPaperColsMode) => Promise<void>;
  onUpgradeVip: () => void;
};

const AIColsSidebarContent = memo(function AIColsSidebarContent({
  collapsed,
  vip,
  aiColsConfig,
  cols,
  onSelected,
  onUpgradeVip,
}: AIColsSidebarContentProps) {
  return (
    <div
      data-target
      hidden={collapsed}
      className="w-full p-4 text-sm h-full relative overflow-x-hidden overflow-y-auto shadow-[0px_3px_8px_0px_rgba(0,0,0,0.16)]"
    >
      <CustomAIColForm>
        {
          vip === 1 ? null : (
            <Image
              onClick={onUpgradeVip}
              src="https://oss.findinai.cn/storage/20260507/7932c3473748189bdac23b7571ce6283.png"
              width={240}
              height={74}
              alt=""
              className="w-full h-auto pb-4 cursor-pointer"
            />
          )
        }
        <div className="flex items-center whitespace-nowrap">
          <span>添加 AI 表格列，快速获得文献信息</span>
          <Tip
            className="ml-1 cursor-help"
            side="right"
            label={(
              <ul className="ml-2 list-disc">
                <li>每生成 1 个单元格
                  <ul className="ml-4 list-disc">
                    <li>创建的 AI 表格列消耗 {aiColsConfig?.customPrice} 个飞豆</li>
                    <li>预设的 AI 表格列消耗 {aiColsConfig?.presetPrice} 个飞豆</li>
                    <li>智能解析表格列消耗 {aiColsConfig?.chartPrice} 个飞豆</li>
                  </ul>
                </li>
                <li>生成过的单元格不再消耗飞豆</li>
                <li>无法解析的文献不会消耗飞豆</li>
              </ul>
            )}
          >
            <HelpCircleIcon />
          </Tip>
          {
            aiColsConfig?.letter?.url ? (
              <UserTriggeredDialog
                width={aiColsConfig?.letter?.width}
                height={aiColsConfig?.letter?.height}
                url={aiColsConfig.letter.url}
              >
                <div className="max-w-fit flex items-center">
                  <Tip label={aiColsConfig.letter.tip}>
                    <LetterIcon className="ml-2 cursor-pointer" />
                  </Tip>
                </div>
              </UserTriggeredDialog>
            ) : null
          }
        </div>
        <div className="mt-3">
          <AIColsSelector
            cols={cols}
            onSelected={onSelected}
          />
        </div>
      </CustomAIColForm>
    </div>
  );
});

const PAPER_TABLE_ROW_ESTIMATE_SIZE = 460;
const PAPER_TABLE_ROW_OVERSCAN = 4;

type PaperRowsVirtualizer = Virtualizer<HTMLDivElement, HTMLTableRowElement>;

type VirtualizedPaperTableBodyProps = {
  rows: Row<Paper>[];
  visibleColumnCount: number;
  rowVirtualizer: PaperRowsVirtualizer;
  pinOverrides: Record<string, string | null | undefined>;
};

function VirtualizedPaperTableBody({
  rows,
  visibleColumnCount,
  rowVirtualizer,
  pinOverrides,
}: VirtualizedPaperTableBodyProps) {
  const virtualRows = rowVirtualizer.getVirtualItems();
  const firstVirtualRow = virtualRows[0];
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const paddingTop = firstVirtualRow?.start ?? 0;
  const paddingBottom = Math.max(
    0,
    rowVirtualizer.getTotalSize() - (lastVirtualRow?.end ?? 0),
  );
  const colSpan = Math.max(visibleColumnCount, 1);

  if (!rows.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell
            colSpan={colSpan}
            className="h-24 text-center"
          >
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {paddingTop > 0 ? (
        <TableRow
          aria-hidden="true"
          noborder
          className="hover:bg-transparent"
          style={{ height: paddingTop }}
        >
          <TableCell colSpan={colSpan} className="border-0 p-0" />
        </TableRow>
      ) : null}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) return null;

        const paper = row.original as Paper;
        const cells = row.getVisibleCells();
        const pinTime = getPaperPinTime(paper, pinOverrides);
        const isPinned = !!pinTime;

        return (
          <TableRow
            key={row.id}
            ref={(node) => {
              if (node) {
                rowVirtualizer.measureElement(node);
              }
            }}
            data-index={virtualRow.index}
            className={cn(
              "relative group/row",
              "bg-background hover:bg-accent-lighten",
              isPinned && "bg-muted/95"
            )}
          >
            {cells.map((cell, i) => (
              <TableCell
                key={cell.id}
                data-row={row.id}
                className={cn(
                  "relative p-5 group-hover/row:bg-accent-lighten max-w-fit group/cell",
                  isPinned && "bg-muted/95",
                  i === 0 && "sticky left-0 z-10 bg-background border-0",
                  i === 0 && "w-10 min-w-10 max-w-10",
                  i === 0 && isPinned && "bg-muted/95",
                  i === 1 && "sticky left-10 z-10 bg-background border-0",
                  i === 1 && "w-[260px] min-w-[260px] max-w-[260px] ",
                  i === 1 && isPinned && "bg-muted/95",
                  i === 1 && cells.length > 2 && "after:absolute after:inset-y-0 after:right-0 after:border-r after:border-border",
                  i === cells.length - 1 && "border-r-0",
                )}
              >
                {flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                )}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
      {paddingBottom > 0 ? (
        <TableRow
          aria-hidden="true"
          noborder
          className="hover:bg-transparent"
          style={{ height: paddingBottom }}
        >
          <TableCell colSpan={colSpan} className="border-0 p-0" />
        </TableRow>
      ) : null}
    </TableBody>
  );
}

const getPaperPinTime = (
  paper: Paper,
  pinOverrides: Record<string, string | null | undefined>,
) => {
  return pinOverrides[paper.paperId] ?? paper.pinTime ?? null;
};

const requestPaperPin = async (
  action: PinAction,
  paperId: PaperId,
  subjectId: string,
) => {
  const params = new URLSearchParams();
  params.append('paper_id[0]', paperId);
  params.append('subject_id', subjectId || '0');

  const response = (await fun.post<WrapResponse<PaperPinPayload, false>>(
    api(action === 'pin' ? '/paper/pin' : '/paper/unpin', true),
    params,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  )).data as WrapResponse<PaperPinPayload, false> & { msg?: string };

  return response;
};

export default function PaperList({
  className = '',
}: PaperListProps) {
  const { subjectId = '' } = usePathParams();
  const {
    importPaper,
  } = useSubjects();
  const {
    userQuotas,
    setBalanceInsufficient,
  } = useUser();
  const {
    colConsumePrice,
    setColConsumePrice,
    setPaperCols,
    refreshPaperCol,
    fetchPaperCols,
    setCitationStyle,
  } = useAiCol();
  const {
    limits,
  } = useUserVip();
  const papers = usePapers(subjectId);
  const cols = useCols(subjectId);
  const {
    pageIndex,
    pageSize,
  } = usePagination(subjectId);
  const papersCitationStyle = usePapersCitationStyle(subjectId);
  const paperPinQuota = usePapersPinQuota(subjectId);
  const listCitationStyleKeyFromStore = useListCitationStyleKey(subjectId);
  const listCitationStyleUseDefault = useListCitationStyleUseDefault(subjectId);

  const dropAreaRef = useRef<HTMLDivElement | null>(null);
  const tableViewportRef = useRef<HTMLDivElement | null>(null);
  const pendingPinRefreshScrollRef = useRef<{ top: number; left: number } | null>(null);

  const { isPolling, restartPolling } = useFetchPapersPolling(subjectId);
  // 使用 ref 稳定易变值，避免 columns 重建
  const isPollingRef = useRef(isPolling);
  useEffect(() => { isPollingRef.current = isPolling; }, [isPolling]);
  const restartPollingRef = useRef(restartPolling);
  useEffect(() => { restartPollingRef.current = restartPolling; }, [restartPolling]);
  const handleRetry = useCallback(({ paperId, keyName, status }: ResultViewRetryPayload) => {
    if (typeof status === 'number' && status < 0) {
      // -5001飞豆余额不足; -30040数据为生成
      // if (status === -5001 || status === -30040) {
      setRefreshCellDailogOpenValues({
        colId: keyName,
        subjectId,
        paperId,
      });
      setRefreshCellDailogOpen(true);
      // }
    } else {
      // 重启轮询
      restartPollingRef.current?.(true);
    }
  }, [subjectId]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [pinOverrides, setPinOverrides] = useState<Record<string, string | null | undefined>>({});
  const [pinPendingByPaperId, setPinPendingByPaperId] = useState<Record<string, PinAction | undefined>>({});
  const [pinLimitOverride, setPinLimitOverride] = useState<number | null>(null);
  const [pinnedCountOverride, setPinnedCountOverride] = useState<number | null>(null);
  const pinLimit = pinLimitOverride ?? paperPinQuota.pinLimit;
  const pinnedCount = pinnedCountOverride ?? paperPinQuota.pinnedCount;
  // 表格模式
  const {
    setPapersGridMode,
    getPapersGridMode,
  } = usePapersGridMode();

  const isSimplifyGrid = getPapersGridMode(subjectId) === 1;
  const setIsSimplifyGrid = useCallback((isSimplifyGrid: boolean) => {
    setPapersGridMode(subjectId, isSimplifyGrid ? 1 : 0)
  }, [setPapersGridMode, subjectId]);

  const {
    toggleSelected,
    toggleCurrentPageRowsSelected,
  } = useSelectionActions(subjectId);

  const {
    getFetchedStatus,
    fetchPapers,
    fetchLoading,
    fetchSilent,
  } = useFetchPapers();
  const fetchedStatus = getFetchedStatus(subjectId);
  // 搜索
  const {
    searchString,
    // 排序
    order,
    setOrder,
  } = useFilters(subjectId);

  const orderRef = useRef(order);

  // 避免函数引用变化导致的重复触发，使用 ref 稳定调用
  const fetchPapersRef = useRef(fetchPapers);
  const fetchPaperColsRef = useRef(fetchPaperCols);
  useEffect(() => { fetchPapersRef.current = fetchPapers; }, [fetchPapers]);
  useEffect(() => { fetchPaperColsRef.current = fetchPaperCols; }, [fetchPaperCols]);
  useEffect(() => {
    (async () => {
      const orderChanged = orderRef.current !== order;
      fetchPaperColsRef.current(subjectId);
      await fetchPapersRef.current({
        subjectId,
        order,
        pageIndex: 0,
      }, true);
      if (orderChanged) {
        orderRef.current = order;
      }
    })();
  }, [subjectId, order]);

  const handleSetPaperCols = useCallback(async (colKeys: string[], cols: Col[], mode?: SetPaperColsMode) => {
    const c = getColConsumePrice(cols, userQuotas?.balance || 0);
    const confirm = async () => {
      setIsSimplifyGrid(false);
      try {
        if (aiColsSetting.current === true) {
          return;
        }
        aiColsSetting.current = true;
        await setPaperCols(colKeys, subjectId, mode);
      } finally {
        aiColsSetting.current = false;
      }
    }
    if ((c.customConsume + c.stdConsume + c.chartConsume) === 0 || mode === 'del') {
      await confirm();
    } else {
      setColConsumePrice(c);
      setConfirmPriceDialogOpen(true);
      setConfrimCallback(() => confirm);
    }
  }, [setColConsumePrice, setIsSimplifyGrid, setPaperCols, subjectId, userQuotas?.balance]);

  const handleUpload = useCallback(() => {
    track('pdf_file_upload', {
      papers: papers.length,
    });
  }, [papers.length]);

  const handleRefresh = useCallback(() => {
    fetchPapersRef.current?.(subjectId, true);
  }, [subjectId]);

  const handlePaperPinChange = useCallback(async (paper: Paper, action: PinAction, rowHeight = 0) => {
    const paperId = paper.paperId;
    if (pinPendingByPaperId[paperId]) return;
    const wasPinned = !!getPaperPinTime(paper, pinOverrides);

    setPinPendingByPaperId((prev) => ({
      ...prev,
      [paperId]: action,
    }));

    try {
      const response = await requestPaperPin(action, paperId, subjectId);
      const message = response.message || response.msg;
      if (response.code !== 0) {
        if (message) {
          toast.error(message);
        }
        return;
      }

      if (action === 'pin') {
        const nextPinTime = new Date().toISOString();
        setPinOverrides((prev) => ({
          ...prev,
          [paperId]: nextPinTime,
        }));
        if (typeof response.data?.pin_limit === 'number') {
          setPinLimitOverride(response.data.pin_limit);
        }
        if (typeof response.data?.pinned_count === 'number') {
          setPinnedCountOverride(response.data.pinned_count);
        }
      } else {
        setPinOverrides((prev) => ({
          ...prev,
          [paperId]: null,
        }));
        if (typeof response.data?.pin_limit === 'number') {
          setPinLimitOverride(response.data.pin_limit);
        }
        if (typeof response.data?.pinned_count === 'number') {
          setPinnedCountOverride(response.data.pinned_count);
        }
      }

      toast.success(action === 'pin' ? '文献已置顶' : '文献已取消置顶');

      const viewport = tableViewportRef.current;
      pendingPinRefreshScrollRef.current = viewport
        ? {
            top: viewport.scrollTop + (action === 'pin' ? rowHeight : 0),
            left: viewport.scrollLeft,
          }
        : null;
      await fetchPapersRef.current?.({
        subjectId,
        order: orderRef.current,
        pageIndex,
        pageSize,
      }, true);
      setPinLimitOverride(null);
      setPinnedCountOverride(null);
    } catch (error) {
      console.error('Error pin paper:', error);
      toast.error(action === 'pin' ? '置顶失败，请稍后重试' : '取消置顶失败，请稍后重试');
    } finally {
      setPinPendingByPaperId((prev) => {
        const next = { ...prev };
        delete next[paperId];
        return next;
      });
    }
  }, [pageIndex, pageSize, pinOverrides, pinPendingByPaperId, subjectId]);

  // 引用格式相关
  const { formatBibliography } = useCiteproc();
  const {
    fetch: fetchCitationStylesConfig,
    value: citationStylesValue,
  } = useCitationStylesConfig();

  useEffect(() => {
    fetchCitationStylesConfig();
  }, [fetchCitationStylesConfig]);

  useEffect(() => {
    if (citationStylesValue) {
      registerCitationStyles(citationStylesValue, API_KEY_TO_STYLE_KEY);
      const { unknownApiKeys, missingApiKeys } = diffCitationStyleApiKeys(
        Object.keys(citationStylesValue),
      );
      if (unknownApiKeys.length || missingApiKeys.length) {
        console.warn('[citation_styles] style_key 与约定不一致', {
          unknownApiKeys,
          missingApiKeys,
        });
      }
    }
  }, [citationStylesValue]);

  const listCitationStyleKey = listCitationStyleKeyFromStore;

  const styleFromList = useMemo((): SupportedStyleKey | null => {
    if (!listCitationStyleKey) return null;
    return toSupportedStyleKey(listCitationStyleKey);
  }, [listCitationStyleKey]);

  const [citeprocStyleOverride, setCiteprocStyleOverride] = useState<SupportedStyleKey | null>(null);
  const [citationSwitchEpoch, setCitationSwitchEpoch] = useState(0);
  const [isCitationStylePending, startCitationStyleTransition] = useTransition();
  const citationSwitchToastRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setCiteprocStyleOverride(null);
  }, [listCitationStyleKey]);

  const isCitationStyleReady = Boolean(
    citeprocStyleOverride ||
    listCitationStyleKey ||
    listCitationStyleUseDefault,
  );
  const citeprocStyle = citeprocStyleOverride ?? styleFromList ?? 'GB/T 7714-2015';

  useEffect(() => {
    if (isCitationStylePending || !citationSwitchToastRef.current) return;

    const toastId = citationSwitchToastRef.current;
    const dismissToast = () => {
      toast.dismiss(toastId);
      citationSwitchToastRef.current = undefined;
    };

    const idleId = typeof window.requestIdleCallback === "function"
      ? window.requestIdleCallback(dismissToast, { timeout: 3000 })
      : window.setTimeout(dismissToast, 0);

    return () => {
      if (typeof idleId === "number") {
        window.clearTimeout(idleId);
      } else {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [isCitationStylePending]);

  const handleCiteprocStyleChange = useCallback((style: SupportedStyleKey) => {
    if (style === citeprocStyle) return;

    const previousOverride = citeprocStyleOverride;
    citationSwitchToastRef.current = toast.loading('引用类型切换中');

    startCitationStyleTransition(() => {
      setCiteprocStyleOverride(style);
      setCitationSwitchEpoch((epoch) => epoch + 1);
    });

    void setCitationStyle(toCitationStyleApiKey(style)).then((ok) => {
      if (!ok) {
        startCitationStyleTransition(() => {
          setCiteprocStyleOverride(previousOverride);
          setCitationSwitchEpoch((epoch) => epoch + 1);
        });
      }
    });
  }, [citeprocStyle, citeprocStyleOverride, setCitationStyle]);

  const citationFormatOptions = useMemo<CiteprocSelectOption[] | undefined>(() => {
    const optionMap = new Map<string, CiteprocSelectOption>();

    const addOption = (apiKey: string, display: string) => {
      const value = (toSupportedStyleKey(apiKey) ?? apiKey) as string;
      if (!optionMap.has(value)) {
        optionMap.set(value, { value, label: display });
      }
    };

    if (papersCitationStyle?.styles) {
      Object.entries(papersCitationStyle.styles).forEach(([key, item]) => {
        addOption(key, item.display);
      });
    }

    if (citationStylesValue) {
      Object.entries(citationStylesValue).forEach(([key, item]) => {
        addOption(key, item.display);
      });
    }

    if (optionMap.size === 0) return undefined;
    return Array.from(optionMap.values());
  }, [papersCitationStyle?.styles, citationStylesValue]);

  const citeprocStyleLabel = useMemo(() => {
    if (!isCitationStyleReady) return '';
    if (!citationFormatOptions) return citeprocStyle;
    const found = citationFormatOptions.find(opt => opt.value === citeprocStyle);
    return found ? found.label : citeprocStyle;
  }, [citationFormatOptions, citeprocStyle, isCitationStyleReady]);

  const headerCiteprocStyle = (citeprocStyleOverride ?? styleFromList ?? 'GB/T 7714-2015') as SupportedStyleKey;

  const citationStyleContextValue = useMemo<CitationStyleContextValue>(() => ({
    citeprocStyle: headerCiteprocStyle,
    isCitationStyleReady,
    citeprocStyleLabel,
    citationFormatOptions,
    onCiteprocStyleChange: handleCiteprocStyleChange,
    citationSwitchEpoch,
    formatBibliography,
    hasCitationFeature: !!citationFormatOptions,
  }), [
    headerCiteprocStyle,
    isCitationStyleReady,
    citeprocStyleLabel,
    citationFormatOptions,
    handleCiteprocStyleChange,
    citationSwitchEpoch,
    formatBibliography,
  ]);
  const getCurrentPaperPinTime = useCallback((paper: Paper) => {
    return getPaperPinTime(paper, pinOverrides);
  }, [pinOverrides]);

  const columns: ColumnDef<Paper>[] = useTableColumns({
    cols,
    isSimplifyGrid,
    subjectId,
    toggleCurrentPageRowsSelected,
    toggleSelected,
    setOrder: setOrder as any,
    handleSetPaperCols,
    handleRetry,
    handleRefresh,
    orderRef,
    isPolling: isPollingRef.current,
    hasCitationFeature: !!citationFormatOptions,
    getPaperPinTime: getCurrentPaperPinTime,
    pinPendingByPaperId,
    pinLimit,
    pinnedCount,
    onPaperPinChange: handlePaperPinChange,
  });

  const table = useReactTable({
    data: papers || [],
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const tableRows = table.getRowModel().rows;
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: tableRows.length,
    getScrollElement: () => tableViewportRef.current,
    estimateSize: () => PAPER_TABLE_ROW_ESTIMATE_SIZE,
    getItemKey: (index) => tableRows[index]?.id ?? index,
    overscan: PAPER_TABLE_ROW_OVERSCAN,
  });

  const scrollTableToTop = useCallback((behavior: ScrollBehavior = 'smooth') => {
    rowVirtualizer.scrollToIndex(0, { align: 'start' });
    tableViewportRef.current?.scrollTo({
      top: 0,
      behavior,
    });
  }, [rowVirtualizer]);

  useEffect(() => {
    rowVirtualizer.measure();
  }, [columns.length, rowVirtualizer, tableRows.length]);

  useEffect(() => {
    const scrollPosition = pendingPinRefreshScrollRef.current;
    if (scrollPosition) {
      pendingPinRefreshScrollRef.current = null;
      tableViewportRef.current?.scrollTo({
        top: scrollPosition.top,
        left: scrollPosition.left,
        behavior: 'auto',
      });
      return;
    }

    if (!fetchSilent) {
      scrollTableToTop('smooth');
    }
  }, [fetchSilent, papers, scrollTableToTop]);

  const [refreshCellDailogOpen, setRefreshCellDailogOpen] = useState(false);
  const [refreshCellDailogOpenValues, setRefreshCellDailogOpenValues] = useState<{
    colId: string;
    subjectId: string;
    paperId: string;
  } | null>(null);
  // refresh、addNewCol、applyNewCols、uploadPapers
  const [confrimPriceDialogOpen, setConfirmPriceDialogOpen] = useState<boolean | string>(false);
  const [confrimCallback, setConfrimCallback] = useState<() => Promise<void>>();
  const [cancelCallback, setCancelCallback] = useState<() => Promise<void>>();

  const {
    fetch: fetchAiColsConfig,
    value: aiColsConfig,
  } = useAiColsConfig();

  useEffect(() => {
    fetchAiColsConfig();
  }, [fetchAiColsConfig]);


  const handleOnBeforeUpload = useCallback<OnBeforeUpload>(async (files) => {
    const fileCounts = files.length;
    const selectedCols = cols
      .filter((col) => col.selected)
      .map((col) => {
        let unitPrice;
        if (col.unitPrice) {
          unitPrice = col.unitPrice;
        } else if (chartColKeys.includes(col.key)) {
          unitPrice = aiColsConfig!.chartPrice;
        } else if (col.isCustom) {
          unitPrice = aiColsConfig!.customPrice;
        } else {
          unitPrice = aiColsConfig!.presetPrice;
        }
        return ({
          ...col,
          cellCount: fileCounts,
          cost: unitPrice * fileCounts,
        });
      });
    const c = getColConsumePrice(selectedCols, userQuotas?.balance || 0);
    if ((c.customConsume + c.stdConsume + c.chartConsume) === 0) {
      // 不需要弹出确认框
      // ignoreAiCol = 0；不忽略，正常上传文献
      return { ignoreAiCol: false };
    }
    setColConsumePrice(c);
    setConfirmPriceDialogOpen('uploadPapers');
    return new Promise((resolve) => {
      setConfrimCallback(() => async () => {
        // 用户确认：ignoreAiCol = false（不忽略，正常上传）
        resolve({ ignoreAiCol: false });
      });
      setCancelCallback(() => async () => {
        // ignoreAiCol = true；忽略
        resolve({ ignoreAiCol: true });
      });
    });
  }, [aiColsConfig, cols, setColConsumePrice, userQuotas?.balance]);


  const aiColsSetting = useRef(false);

  const { vip } = useUserVip();
  const { upgradeVip } = useUpgradeVip();

  const {
    collapsed,
  } = useAiColSidebarCollapsed(subjectId);

  // 监听上传任务状态，用于显示 loading
  const { getAllUploadTasks } = useUploadSessions();
  const uploadTasks = getAllUploadTasks();
  const isUploading = useMemo(() => {
    return uploadTasks.some(
      (task) => task.status === "validating" || task.status === "uploading"
    );
  }, [uploadTasks]);

  // 第一次加载中
  if (!limits || fetchedStatus === 0 || fetchedStatus === -1) {
    return (
      <Loading />
    );
  }
  if (fetchedStatus === 1 && (papers.length || (!papers.length && searchString))) {
    return (
      <div
        ref={dropAreaRef}
        className={cn(
          "group flex flex-col max-w-full h-full relative data-[dragging=true]:ring-primary data-[dragging=true]:ring-2 p-5 bg-background rounded-lg overflow-hidden",
          className,
        )}
      >
        <Toolbar
          className="flex items-center justify-between overflow-hidden flex-wrap gap-4"
          dropArea={dropAreaRef.current}
          onUploaded={handleUpload}
          handleOnBeforeUpload={handleOnBeforeUpload}
          isSimplifyGrid={isSimplifyGrid}
          setIsSimplifyGrid={setIsSimplifyGrid}
          onNewColsApplied={async (confirm, cols) => {
            const c = getColConsumePrice(cols, userQuotas?.balance || 0)
            if ((c.customConsume + c.stdConsume + c.chartConsume) === 0) {
              await confirm();
            } else {
              setColConsumePrice(c);
              setConfrimCallback(() => confirm);
              setConfirmPriceDialogOpen(true);
            }
          }}
        />
        <div className="flex flex-1 overflow-hidden mt-4 relative">
          {/* 表格的滚动条 */}
          <CitationStyleProvider value={citationStyleContextValue}>
          <div className="w-full rounded-lg border h-full flex overflow-y-auto overflow-x-hidden relative justify-between">
            {fetchLoading && !fetchSilent ? <Loading mask zIndex={31} /> : null}
            <div className="flex-1 flex flex-row justify-between overflow-hidden">
              <div ref={tableViewportRef} className="flex overflow-y-auto flex-1">
                <Table className={[
                  "rounded-l-lg table-auto relative w-fit min-w-full bg-background",
                ].filter(Boolean).join(' ')}>
                  <TableHeader className={cn(
                    "sticky top-0 z-20 border-0",
                    "after:absolute after:inset-x-0 after:bottom-0 after:border-b after:border-border",
                  )}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow
                        key={headerGroup.id}
                        className="bg-muted/95 text-foreground hover:bg-muted"
                      >
                        {headerGroup.headers.map((header, i) => {
                          // const render = header.column.columnDef.header as Function;
                          return (
                            <TableHead
                              key={header.id}
                              className={cn(
                                "text-foreground",
                                i === 0 && "sticky left-0 z-30 bg-muted border-0 pl-5",
                                i === 0 && "w-10 min-w-10 max-w-10",
                                i === 1 && "sticky left-10 z-30 bg-muted border-0",
                                i === 1 && "w-[260px] min-w-[260px] max-w-[260px]",
                                i === 1 && headerGroup.headers.length > 2 && "after:absolute after:inset-y-0 after:right-0 after:border-r after:border-border",
                              )}
                            >
                              {
                                flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )
                              }
                              {/* {
                                render(header.getContext())
                              } */}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <VirtualizedPaperTableBody
                    rows={tableRows}
                    visibleColumnCount={visibleColumnCount}
                    rowVirtualizer={rowVirtualizer}
                    pinOverrides={pinOverrides}
                  />
                </Table>
              </div>
              {
                !isSimplifyGrid && (
                  <div className={cn(
                    "h-full relative z-20",
                    !collapsed && "duration-100 w-[300px]",
                    collapsed && "duration-200 w-[10px]",
                  )}>
                    <SemiCircleWithShadow />
                    <AIColsSidebarContent
                      collapsed={collapsed}
                      vip={vip}
                      aiColsConfig={aiColsConfig}
                      cols={cols}
                      onSelected={handleSetPaperCols}
                      onUpgradeVip={upgradeVip}
                    />
                  </div>
                )
              }
              {
                papers.length ? null : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 pointer-events-none">
                    <Image src="https://oss.findinai.cn/storage/20250817/b86520ca0eb7ec98561dcda49e4b37c3.png" width="150" height="150" alt="没有搜索到结果" />
                    <div className="text-annotation-foreground w-1/3 text-center text-sm">
                      <div>没有搜索到“{searchString}”</div>
                      <div>换个关键词试试吧</div>
                    </div>
                  </div>
                )
              }
            </div>
          </div>
          </CitationStyleProvider>
        </div>
        <div className="relative h-fit min-h-5 mt-2">
          <TableFooterbar subjectId={subjectId} onTurnPage={() => {
            scrollTableToTop('smooth');
          }} />
        </div>
        <RefreshCellDialog
          open={refreshCellDailogOpen}
          setOpen={setRefreshCellDailogOpen}
          onConfirm={async (value) => {
            if (!refreshCellDailogOpenValues) return;
            const { colId, paperId, subjectId } = refreshCellDailogOpenValues;
            const mode = 'budget';
            // 定义参数映射
            const paramMap: Record<string, [string, string, string, string]> = {
              cell: [colId, subjectId, paperId, mode],
              col: [colId, subjectId, '', mode],
              row: ['', subjectId, paperId, mode],
              table: ['', subjectId, '', mode],
            };
            const params = paramMap[value];
            if (params) {
              await refreshPaperCol(...params);
              setConfrimCallback(() => async () => {
                if (!refreshCellDailogOpenValues) return;
                const { colId, paperId, subjectId } = refreshCellDailogOpenValues;
                // 定义参数映射
                const paramMap: Record<string, [string, string, string]> = {
                  cell: [colId, subjectId, paperId],
                  col: [colId, subjectId, ''],
                  row: ['', subjectId, paperId],
                  table: ['', subjectId, ''],
                };
                const params = paramMap[value];
                if (params) {
                  await refreshPaperCol(...params);
                }
              })
              setConfirmPriceDialogOpen(true);
            }
          }}
        />
        {!!confrimPriceDialogOpen ? (
          <ConfirmPriceDialog
            colConsumePrice={colConsumePrice}
            subjectId={subjectId}
            open={!!confrimPriceDialogOpen}
            setOpen={setConfirmPriceDialogOpen}
            cancel={() => {
              cancelCallback?.();
              setConfirmPriceDialogOpen(false);
            }}
            confirm={async (insufficient) => {
              if (insufficient) {
                setBalanceInsufficient(true);
              } else {
                await confrimCallback?.();
              }
              setConfirmPriceDialogOpen(false);
            }}
            title={confrimPriceDialogOpen === 'uploadPapers' ? 'AI 表格列预计消耗' : ''}
            cancelLabel={confrimPriceDialogOpen === 'uploadPapers' ? '仅上传文献' : ''}
          />
        ) : null}
        {/**
         * 与 PaperUpload / useUpload 的协作说明：
         * 
         * - 当外层判断需要在上传前提示「AI 表格列预计消耗」时，会把 confrimPriceDialogOpen 设为 'uploadPapers'
         * - 此时 confrimCallback / cancelCallback 一般由 onBeforeUpload(files) 填充：
         *   - cancelCallback：只上传文献，不消耗 AI 列（通常对应 onBeforeUpload 返回 true）
         *   - confrimCallback：确认同时消耗 AI 列，再继续调用 useUpload 内部的上传逻辑（通常对应 onBeforeUpload 返回 false/undefined）
         * - 这样，业务层可以在真正开始上传前插入「预算确认」对话框，而 useUpload 只关心 onBeforeUpload 的返回值
         * - 重要：onBeforeUpload 在 validateFiles 之前执行，因此用户确认对话框时，尚未创建任何 UploadItem，
         *   面板不会显示，用户可以在确认对话框中安心选择，不会看到面板突然弹出
         */}
      </div>
    );
  } else {
    // 上传图片及功能介绍
    return (
      <div ref={dropAreaRef} className="group bg-background rounded-lg data-[dragging=true]:border-primary data-[dragging=true]:border h-full w-full p-10 overflow-auto relative">
        {isUploading && <Loading mask zIndex={50} />}
        <div className="flex flex-col relative items-center justify-center   h-fit w-fit min-h-full min-w-full">
          <PaperUpload
            variant="area"
            plugins={subjectId ? ["url", {
              plugin: importPlugin,
              props: {
                onImport: async (paperIds: PaperId[], papers: Paper[]) => {
                  await importPaper(subjectId, paperIds);
                  return true;
                }
              }
            }, "local-folder", "local-file"] : ["url", "local-folder", "local-file"]}
            subjectId={subjectId}
            route={typeof window !== "undefined" ? window.location.pathname : ""}
            onBeforeUpload={handleOnBeforeUpload}
            onUploaded={handleUpload}
            dropArea={dropAreaRef}
            className="w-[40%] min-w-fit shrink-0 items-center justify-center"
          />
          <PapersFeatures className="mt-10 md:mt-16 pb-4" />
        </div>
        {!!confrimPriceDialogOpen ? (
          <ConfirmPriceDialog
            colConsumePrice={colConsumePrice}
            subjectId={subjectId}
            open={!!confrimPriceDialogOpen}
            setOpen={setConfirmPriceDialogOpen}
            cancel={() => {
              cancelCallback?.();
              setConfirmPriceDialogOpen(false);
            }}
            confirm={async (insufficient) => {
              if (insufficient) {
                setBalanceInsufficient(true);
              } else {
                await confrimCallback?.();
              }
              setConfirmPriceDialogOpen(false);
            }}
            title={confrimPriceDialogOpen === 'uploadPapers' ? 'AI 表格列预计消耗' : ''}
            cancelLabel={confrimPriceDialogOpen === 'uploadPapers' ? '仅上传文献' : ''}
          />
        ) : null}
      </div>
    );
  }
};

export function RefreshCellDialog({
  open,
  setOpen,
  onConfirm,
}: {
  open: boolean,
  setOpen: (open: boolean) => void,
  onConfirm: (value: string) => Promise<void>;
}) {
  const options = useMemo(() => {
    return [{
      value: 'cell',
      label: '当前单元格',
    }, {
      value: 'col',
      label: '当前 AI 列',
    }, {
      value: 'row',
      label: '当前文献',
    }, {
      value: 'table',
      label: '完整AI表格',
    }];
  }, []);
  const [select, setSelect] = useState(options[0].value);
  useEffect(() => {
    if (!open) {
      setSelect(options[0].value);
    }
  }, [open, options]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-base">刷新范围</DialogTitle>
          <DialogDescription>本次操作仅针对待刷新的单元格（已生成的不受影响）</DialogDescription>
        </DialogHeader>
        <div className="text-sm">
          <RadioGroup
            className="space-y-3"
            value={select}
            onValueChange={(v) => {
              setSelect(v);
            }}
          >
            {
              options.map(({ value, label }) => {
                return (
                  <div key={value} className="flex items-center space-x-2 ">
                    <RadioGroupItem value={value} id={value} />
                    <Label htmlFor={value} className="cursor-pointer">{label}</Label>
                  </div>
                )
              })
            }
          </RadioGroup>
        </div>
        <DialogFooter className="gap-4">
          <Button
            variant="outline"
            onClick={() => { setOpen(false) }}
          >
            取消
          </Button>
          <Button
            className="min-w-[8.125rem]"
            onClick={async () => {
              await onConfirm(select);
              setOpen(false);
            }}
          >
            刷新
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
